'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { restoreSessionToLocalStorage } from '@/lib/sessionService';

interface Session {
  id: string
  industry: string
  entry_who: string
  viability_score: number
  verdict: string
  created_at: string
  screen_reached: number
}

export default function SmartDashboard() {
  const router = useRouter()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const lastSession = sessions[0] || null

  useEffect(() => {
    fetch('/api/sessions')
      .then(r => r.json())
      .then(data => {
        setSessions(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  function continueSession(session: Session) {
    // Restore all session data to localStorage from DB session object
    restoreSessionToLocalStorage(session as unknown as Record<string, unknown>);
    // Also ensure on_industry_full is set (may not be in restoreSessionToLocalStorage if industry is missing)
    if (session.industry) {
      localStorage.setItem('on_industry_full', JSON.stringify({ name: session.industry }));
    }
    const routes: Record<number, string> = {
      1: '/discovery',
      2: '/',
      3: '/market-sizing',
      4: '/validation',
      5: '/execution-plan'
    }
    sessionStorage.setItem('from_dashboard', 'true')
    router.push(routes[session.screen_reached] || '/execution-plan')
  }

  function newScan() {
    const keys = [
      'on_industry_full', 'on_entry',
      'on_market', 'on_validation',
      'on_execution', 'orbital_industry',
      'on_market_cache', 'on_entry_cache',
      'on_validation_cache', 'on_discovery_results'
    ]
    keys.forEach(k => localStorage.removeItem(k))
    sessionStorage.setItem('from_dashboard', 'true')
    router.push('/discovery')
  }

  const verdictColor = (v: string) =>
    v === 'SHIP' ? '#00ff88' :
    v === 'KILL' ? '#ff4d4d' : '#f5a623'

  const verdictBg = (v: string) =>
    v === 'SHIP' ? 'rgba(0,255,136,0.08)' :
    v === 'KILL' ? 'rgba(255,77,77,0.08)' :
    'rgba(245,166,35,0.08)'

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en', {
      month: 'short', day: 'numeric', year: 'numeric'
    })
  }

  const sessionAge = (createdAt: string) => {
    const diffDays = Math.floor(
      (Date.now() - new Date(createdAt).getTime())
      / (1000 * 60 * 60 * 24)
    )
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return `${Math.floor(diffDays / 7)} weeks ago`
  }

  const isStaleSession = (createdAt: string) => {
    const diffDays = Math.floor(
      (Date.now() - new Date(createdAt).getTime())
      / (1000 * 60 * 60 * 24)
    )
    return diffDays > 7
  }

  return (
    <div style={{
      maxWidth: '640px',
      margin: '0 auto',
      padding: '32px 16px',
      paddingBottom: 'calc(80px + env(safe-area-inset-bottom))'
    }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: '10px',
          color: '#3a3a4a',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          marginBottom: '6px'
        }}>
          OrbitalNiche · Vietnam Intelligence
        </div>
        <div style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: '22px',
          fontWeight: 700,
          color: '#e8e8f0',
          letterSpacing: '-0.5px'
        }}>
          {loading ? 'Loading...' : lastSession ?'Welcome back': 'Start your research'}
        </div>
      </div>

      {/* Last Session Card */}
      {lastSession && (
        <div style={{
          background: verdictBg(lastSession.verdict),
          border: `1px solid ${verdictColor(lastSession.verdict)}33`,
          borderRadius: '12px',
          padding: '18px',
          marginBottom: '16px'
        }}>
          <div style={{
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '9px',
            color: '#6b6b80',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            marginBottom: '10px'
          }}>
            Last Session · {formatDate(lastSession.created_at)}
          </div>

          <div style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#e8e8f0',
            marginBottom: '4px'
          }}>
            {lastSession.industry}
          </div>

          <div style={{
            fontSize: '12px',
            color: '#6b6b80',
            marginBottom: '14px'
          }}>
            {lastSession.entry_who || '—'}
          </div>

          {/* Scan age */}
          <div style={{
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '10px',
            color: '#6b6b80',
            marginBottom: '8px'
          }}>
            Scanned {sessionAge(lastSession.created_at)}
          </div>

          {/* Stale warning */}
          {isStaleSession(lastSession.created_at) && (
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: '10px',
              color: '#f5a623',
              marginBottom: '12px'
            }}>
              ⚠ Market may have changed — consider rescanning
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: '20px',
                fontWeight: 700,
                color: verdictColor(lastSession.verdict)
              }}>
                {lastSession.viability_score}/100
              </span>
              <span style={{
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: '10px',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: '20px',
                background: verdictBg(lastSession.verdict),
                color: verdictColor(lastSession.verdict),
                border: `1px solid ${verdictColor(lastSession.verdict)}44`
              }}>
                {lastSession.verdict}
              </span>
            </div>
            <button
              onClick={() => continueSession(lastSession)}
              style={{
                background: verdictColor(lastSession.verdict),
                color: '#0a0a0a',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        marginBottom: '28px'
      }}>
        <button
          onClick={newScan}
          style={{
            background: '#00ff88',
            color: '#0a0a0a',
            border: 'none',
            borderRadius: '10px',
            padding: '16px',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          + New Scan
          <div style={{
            fontSize: '10px',
            fontWeight: 400,
            marginTop: '4px',
            opacity: 0.7
          }}>
            Fresh market research
          </div>
        </button>

        <button
          onClick={() => router.push('/history')}
          style={{
            background: 'transparent',
            color: '#e8e8f0',
            border: '1px solid #1a1a1a',
            borderRadius: '10px',
            padding: '16px',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          View History
          <div style={{
            fontSize: '10px',
            fontWeight: 400,
            marginTop: '4px',
            color: '#6b6b80'
          }}>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''} saved
          </div>
        </button>
      </div>

      {/* Recent Sessions List */}
      {sessions.length > 0 && (
        <div>
          <div style={{
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '10px',
            color: '#3a3a4a',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            marginBottom: '10px'
          }}>
            Recent Sessions
          </div>

          {sessions.slice(0, 5).map((s, i) => (
            <div
              key={s.id || i}
              onClick={() => continueSession(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                background: '#0d0d0d',
                border: '1px solid #1a1a1a',
                borderRadius: '8px',
                marginBottom: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: '10px',
                color: '#3a3a4a',
                width: '20px',
                flexShrink: 0
              }}>
                {String(i + 1).padStart(2, '0')}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#e8e8f0',
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {s.industry}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b6b80',
                  fontFamily: 'IBM Plex Mono, monospace'
                }}>
                  {formatDate(s.created_at)}
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#3a3a4a',
                  fontFamily: 'IBM Plex Mono, monospace'
                }}>
                  {sessionAge(s.created_at)}
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '4px',
                flexShrink: 0
              }}>
                <span style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: verdictColor(s.verdict)
                }}>
                  {s.viability_score}
                </span>
                <span style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: '20px',
                  background: verdictBg(s.verdict),
                  color: verdictColor(s.verdict)
                }}>
                  {s.verdict}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && sessions.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: '12px',
          color: '#3a3a4a'
        }}>
          No sessions yet.
          <br />
          <span style={{ color: '#00ff88', marginTop: '8px', display: 'inline-block' }}>
            Start your first scan above.
          </span>
        </div>
      )}
    </div>
  )
}
